import React, { useContext, useEffect, useRef, useState } from "react";
import { API_URL } from "src/scenes/App";
import axios from "axios";
import styled from "styled-components";
import {
    Toast, FlexContainer, FormField, ErrorText,
    Form, LottieLoadingIndicator, CustomSelect,
    PortalFormActions, PortalFormScrollBody, PortalFormSection,
    PortalFormSectionDescription, PortalFormSectionHeader,
    PortalFormSectionTitle, PortalFormSurface, PortalFormDescription,
    PortalFormFieldGroup, PortalFormFieldRow, PortalFormLabel,
    StandardModalActionButton,
    StandardModalActionGroup, portalColors
} from "src/components";

import useAds from "./hooks/useAds";
import { AdContext } from "../AdContext";
import { AdFormContext } from "./AdFormContext";
import { UserContext } from "src/scenes/App/UserContext";
import pluralize from "pluralize";
import { customNames, capitalizeString } from "src/utils/helpers";
import { ErrorList, ErrorStrings, PLACEMENT_OPTIONS } from "../components/Enums";
import { UserKeys } from "src/constants/userDetails";
import { AUDIENCE, FILTER_BY } from "../Advertising";

import Skeleton from "src/scenes/Statistics/components/Skeleton";
import ValidatedDateInput from "src/components/ValidatedDateInput";
import AdPrimaryImageUpload from "./components/AdPrimaryImageUpload";
import AdVerticalImageUpload from "./components/AdVerticalImageUpload";
import ImageSlider from "./components/ImageSlider";
import { FormAssetSection, FormRightSection, PhoneContainer, TextArea } from "./components/StyledAdComponents";

const AD_AUDIENCE_OPTIONS = [
    { value: "Employees", label: `${capitalizeString(pluralize(customNames.employees))}` },
    { value: "Clients", label: `${capitalizeString(pluralize(customNames.clients))}` },
    { value: "Employees & Clients", label: `${capitalizeString(pluralize(customNames.employees))} & ${capitalizeString(pluralize(customNames.clients))}` }
];
const DEFAULT_AD_AUDIENCE = AD_AUDIENCE_OPTIONS[0].value;

const AdDatePickerSpacer = styled.div`
    height: ${p => p.$active ? "18rem" : "0"};
    transition: height 0.16s ease;
`;

// Form Components Fields
const HeadlineField = (props) => {
    const { headline, inputHeadline, inputErrors } = props;

    const getErrorStrings = (...errs) => inputErrors.filter(err => errs.includes(err)).map(err => ErrorStrings[err]);
    const headlineError = getErrorStrings(ErrorList.EMPTY_HEADLINE, ErrorList.HEADLINE_LIMIT);

    return (
        <PortalFormFieldGroup>
            <PortalFormLabel>
                Headline
            </PortalFormLabel>
            <PortalFormDescription>
                Make it catchy and relevant (up to 60 characters).
            </PortalFormDescription>
            <FormField 
                borderRadius="8px"
                size="0.9375rem"
                padding="10px 20px" 
                height="46px"
                placeholderColor={portalColors.textSoft}
                border={`1px solid ${portalColors.borderStrong}`}
                placeholderAlignment="left"
                width="100%"
                type="text"
                style={{ textAlign: "left", margin: "0px" }}
                placeholder="Enter your ad headline here"
                onChange={(event) => { inputHeadline(event.target.value); }}
                errors={headlineError}
                value={headline}
                showErrors={headlineError.length > 0}
            >
            </FormField>
        </PortalFormFieldGroup>
    );
};

const ButtonTextField = (props) => {
    const { buttonText, inputButtonText, inputErrors } = props;

    const getErrorStrings = (...errs) => inputErrors.filter(err => errs.includes(err)).map(err => ErrorStrings[err]);
    const buttonTextError = getErrorStrings(ErrorList.EMPTY_BUTTON_TEXT, ErrorList.BUTTON_TEXT_LIMIT);

    return (
        <PortalFormFieldGroup>
            <PortalFormLabel>
                Button Text
            </PortalFormLabel>
            <PortalFormDescription>
                Make it catchy and relevant (up to 25 characters).
            </PortalFormDescription>
            <FormField 
                borderRadius="8px"
                size="0.9375rem"
                padding="10px 20px" 
                height="46px"
                placeholderColor={portalColors.textSoft}
                border={`1px solid ${portalColors.borderStrong}`}
                placeholderAlignment="left"
                width="100%"
                type="text"
                style={{ textAlign: "left", margin: "0px" }}
                placeholder='Enter button text (e.g., "Learn More")'
                onChange={(event) => { inputButtonText(event.target.value); }}
                errors={buttonTextError}
                value={buttonText}
                showErrors={buttonTextError.length > 0}
            >
            </FormField>
        </PortalFormFieldGroup>
    );
};

const ButtonLinkField = (props) => {
    const { buttonLink, inputButtonLink, inputErrors } = props;

    const getErrorStrings = (...errs) => inputErrors.filter(err => errs.includes(err)).map(err => ErrorStrings[err]);
    const buttonLinkError = getErrorStrings(ErrorList.EMPTY_BUTTON_LINK);

    return (
        <PortalFormFieldGroup>
            <PortalFormLabel>
                Button Link
            </PortalFormLabel>
            <PortalFormDescription>
                The link where users will be directed when they click the button
            </PortalFormDescription>
            <FormField 
                borderRadius="8px"
                size="0.9375rem"
                padding="10px 20px" 
                height="46px"
                placeholderColor={portalColors.textSoft}
                border={`1px solid ${portalColors.borderStrong}`}
                placeholderAlignment="left"
                width="100%"
                type="text"
                style={{ textAlign: "left", margin: "0px" }}
                placeholder="Enter the URL for your button link"
                onChange={(event) => { inputButtonLink(event.target.value); }}
                errors={buttonLinkError}
                value={buttonLink}
                showErrors={buttonLinkError.length > 0}
            >
            </FormField>
        </PortalFormFieldGroup>
    );
};

const AdDurationFields = (props) => {
    const { startsAt, endsAt, inputStartDate, inputFinishDate, inputErrors } = props;
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const getErrorStrings = (...errs) => inputErrors.filter(err => errs.includes(err)).map(err => ErrorStrings[err]);

    const surveyStartDateError = getErrorStrings(ErrorList.EMPTY_START_DATE);
    const surveyEndDateError = getErrorStrings(ErrorList.EMPTY_END_DATE);

    const startDateInvalid = getErrorStrings(ErrorList.DATE_START_DATE_INVALID);
    const finishDateInvalid = getErrorStrings(ErrorList.DATE_END_DATE_INVALID);

    const dateStartFormatInvalid = getErrorStrings(ErrorList.DATE_START_FORMAT_INVALID);
    const dateFinishFormatInvalid = getErrorStrings(ErrorList.DATE_END_FORMAT_INVALID);

    const dateRangeInvalid = getErrorStrings(ErrorList.DATE_RANGE_INVALID);

    const hasAnySurveyDurationErrors = (surveyStartDateError.length > 0 || surveyEndDateError.length > 0 || startDateInvalid.length > 0 || finishDateInvalid.length > 0 || dateStartFormatInvalid.length > 0 || dateFinishFormatInvalid.length > 0 || dateRangeInvalid.length > 0);
    const today = new Date();
    return (
        <PortalFormFieldGroup>
            <PortalFormLabel>
                Campaign Dates
            </PortalFormLabel>
            <PortalFormDescription>
                Choose when your ad campaign will begin and end.
            </PortalFormDescription>
            <PortalFormFieldRow>
                <PortalFormFieldGroup>
                    <PortalFormLabel size="0.875rem" weight={700}>
                        Start Date
                    </PortalFormLabel>
                    <ValidatedDateInput
                        placeholder="Select start date"
                        onChange={(event) => { inputStartDate(event); }}
                        errors={surveyStartDateError}
                        value={startsAt}
                        disabled={false}
                        minDate={today}
                        showErrors={hasAnySurveyDurationErrors}
                        size={"0.938rem"}
                        fontWeight={"400"}
                        onCalendarOpen={() => setIsDatePickerOpen(true)}
                        onCalendarClose={() => setIsDatePickerOpen(false)}
                    />
                </PortalFormFieldGroup>
                <PortalFormFieldGroup>
                    <PortalFormLabel size="0.875rem" weight={700}>
                        End Date
                    </PortalFormLabel>
                    <ValidatedDateInput
                        placeholder="Select end date"
                        onChange={(event) => { inputFinishDate(event); }}
                        errors={surveyEndDateError}
                        value={endsAt}
                        disabled={false}
                        minDate={startsAt}
                        showErrors={hasAnySurveyDurationErrors}
                        size="0.938rem"
                        fontWeight="400"
                        onCalendarOpen={() => setIsDatePickerOpen(true)}
                        onCalendarClose={() => setIsDatePickerOpen(false)}
                    />
                </PortalFormFieldGroup>
            </PortalFormFieldRow>
            <AdDatePickerSpacer $active={isDatePickerOpen} />

            {hasAnySurveyDurationErrors &&
                <ErrorText errorMargin="0px">
                    {startDateInvalid.length > 0 ? startDateInvalid : null}
                    {finishDateInvalid.length > 0 ? finishDateInvalid : null}
                    {dateStartFormatInvalid.length > 0 ? dateStartFormatInvalid : null}
                    {dateFinishFormatInvalid.length > 0 ? dateFinishFormatInvalid : null}
                    {dateRangeInvalid.length > 0 ? dateRangeInvalid : null}
                </ErrorText>
            }
        </PortalFormFieldGroup>
    );
};

const AudienceSelectionField = (props) => {
    const { inputErrors, selectAudience, loading, audience } = props;

    const getErrorStrings = (...errs) => inputErrors.filter(err => errs.includes(err)).map(err => ErrorStrings[err]);
    const audienceError = getErrorStrings(ErrorList.EMPTY_AUDIENCE);

    return (
        <PortalFormFieldGroup>
            <PortalFormLabel>
                Audience Selection
            </PortalFormLabel>
            <PortalFormDescription>
                You can target {capitalizeString(pluralize(customNames.employees))}, {capitalizeString(pluralize(customNames.clients))}, or both.
            </PortalFormDescription>
            <FlexContainer>
                { loading ?
                    <Skeleton height="50px" width="100%" animation="wave" />
                    :
                    <CustomSelect
                        defaultValue={AD_AUDIENCE_OPTIONS[0]}
                        placeholder={"Choose who can see this ad"}
                        value={ AD_AUDIENCE_OPTIONS.find(obj => obj.value === audience) }
                        options={ AD_AUDIENCE_OPTIONS }
                        onChange={(choice) => selectAudience(choice.value)}
                        hasError={audienceError.length > 0}
                    />
                }
                <ErrorText style={{ padding: "10px 20px", margin: "0px" }}>
                    {audienceError}
                </ErrorText>
            </FlexContainer>
        </PortalFormFieldGroup>
    );
};

const PlacementSelectionField = (props) => {
    const { inputErrors, selectPlacement, loading, placement } = props;

    const getErrorStrings = (...errs) => inputErrors.filter(err => errs.includes(err)).map(err => ErrorStrings[err]);
    const placementError = getErrorStrings(ErrorList.EMPTY_PLACEMENT);

    const userContext = useContext(UserContext);
    const userFeatureFlags = userContext?.featureFlags || [];

    const filteredPlacementOptions = getFilteredPlacementOptions(
        PLACEMENT_OPTIONS,
        userFeatureFlags
    );

    return (
        <PortalFormFieldGroup>
            <PortalFormLabel>
                Ad Placement
            </PortalFormLabel>
            <PortalFormDescription>
                Choose where your ad will appear in the app.
            </PortalFormDescription>
            <FlexContainer>
                { loading ?
                    <Skeleton height="50px" width="100%" animation="wave" />
                    :
                    <CustomSelect
                        isMulti
                        placeholder={"Where to display"}
                        value={ placement }
                        options={filteredPlacementOptions}
                        onChange={(choice) => selectPlacement(choice) }
                        hasError={placementError.length > 0}
                        showCheckboxes
                    />
                }
                <ErrorText style={{ padding: "10px 20px", margin: "0px" }}>
                    {placementError}
                </ErrorText>
            </FlexContainer>
        </PortalFormFieldGroup>
    );
};

const AdDescriptionField = (props) => {
    const { description, inputDescription, inputErrors } = props;
    const getErrorStrings = (...errs) => inputErrors.filter(err => errs.includes(err)).map(err => ErrorStrings[err]);
    const descriptionError = getErrorStrings(ErrorList.EMPTY_DESCRIPTION, ErrorList.DESCRIPTION_LIMIT);

    return (
        <PortalFormFieldGroup>
            <PortalFormLabel>
                Ad Description
            </PortalFormLabel>
            <PortalFormDescription>
                Provide more details about your ad.
            </PortalFormDescription>
            <TextArea
                placeholder="Enter your ad description here"
                onChange={(event) => { inputDescription(event.target.value); }}
                errors={descriptionError}
                value={description}
                isHighlighted={descriptionError.length > 0}
                fontSize="0.938rem"
            />
            <ErrorText style={{ padding: "10px 20px", margin: "0px" }}>
                {descriptionError}
            </ErrorText>
        </PortalFormFieldGroup>
    );
};

const getFilteredPlacementOptions = (options, featureFlags = []) => {
    const flags = new Set(featureFlags);

    return options.filter(option => {
        if (!option.featureFlag) return true;
        return flags.has(option.featureFlag);
    });
};

const getEmptyCrop = () => ({
    x: 0,
    y: 0,
    width: 0,
    height: 0
});

const getExistingPrimaryAssetUrl = (formPrimaryAsset, adToEdit) => (
    formPrimaryAsset.primaryAsset?.assetUrl
    || formPrimaryAsset.primaryAssetFile
    || formPrimaryAsset.primaryAssetBlob
    || adToEdit?.primaryAsset?.assetUrl
    || ""
);

const getExistingPrimaryAsset = (formPrimaryAsset, formPrimaryAssetType, adToEdit) => {
    const existingAsset = formPrimaryAsset.primaryAsset?.assetUrl ? formPrimaryAsset.primaryAsset : adToEdit?.primaryAsset;

    return {
        assetUrl: getExistingPrimaryAssetUrl(formPrimaryAsset, adToEdit),
        aspectRatio: existingAsset?.aspectRatio || "widescreen",
        trimStart: existingAsset?.trimStart ?? 0,
        trimEnd: existingAsset?.trimEnd ?? 0,
        crop: existingAsset?.crop || getEmptyCrop(),
        videoKey: existingAsset?.videoKey || null,
        assetType: existingAsset?.assetType || formPrimaryAssetType || "image",
    };
};

const getExistingVerticalAsset = (formVerticalAsset, formVerticalAssetType, adToEdit) => {
    const existingAsset = formVerticalAsset.verticalAsset?.assetUrl ? formVerticalAsset.verticalAsset : adToEdit?.verticalAsset;

    if (!existingAsset?.assetUrl) {
        return null;
    }

    return {
        assetUrl: existingAsset.assetUrl,
        aspectRatio: existingAsset.aspectRatio || "portrait",
        trimStart: existingAsset.trimStart ?? 0,
        trimEnd: existingAsset.trimEnd ?? 0,
        crop: existingAsset.crop || getEmptyCrop(),
        videoKey: existingAsset.videoKey || null,
        assetType: existingAsset.assetType || formVerticalAssetType || "image",
    };
};

const AdvertisingForm = (props) => {
    const { adToEdit, fetchCompanyAds, handleHideAdForm, setFilterOption, setAudienceOption } = props;
    const currentUser = useContext(UserContext);
    const userFeatureFlags = currentUser?.featureFlags || [];

    const filteredPlacementOptions = getFilteredPlacementOptions(
        PLACEMENT_OPTIONS,
        userFeatureFlags
    );

    const {
        requestS3PresignedUrl,
        uploadToS3,
        validateAd,
        getLabelForPreview
    } = useAds(currentUser.token);
    const controllerRef = useRef(new AbortController());
    const { companyColors, companyColorsReady } = useContext(AdContext);
    const [inputErrors, setInputErrors] = useState([]);

    const [headline, setHeadline] = useState("");
    const [description, setDescription] = useState("");
    const [buttonText, setButtonText] = useState("");
    const [buttonLink, setButtonLink] = useState("");
    const [audience, setAudience] = useState(DEFAULT_AD_AUDIENCE);
    const [placement, setPlacement] = useState([]);
    const [startsAt, setStartsAt] = useState("");
    const [endsAt, setEndsAt] = useState("");
    const [formPrimaryAsset, setFormPrimaryAsset] = useState({
        primaryAsset: {},
        primaryAssetFile: "",
        primaryAssetBlob: "",
        crop: null,
        trimStart: null,
        trimEnd: null,
        aspectRatio: null,
    });
    const [formPrimaryAssetChanged, setFormPrimaryAssetChanged] = useState(false);
    const [formPrimaryAssetType, setFormPrimaryAssetType] = useState("");
    const [formVerticalAsset, setFormVerticalAsset] = useState({
        verticalAsset: {},
        verticalAssetFile: "",
        verticalAssetBlob: "",
        crop: null,
        trimStart: null,
        trimEnd: null,
        aspectRatio: null,
    });
    const [formVerticalHasAsset, setFormVerticalHasAsset] = useState(false);
    const [formVerticalAssetType, setFormVerticalAssetType] = useState("");
    const [isFormSending, setIsFormSending] = useState(false);

    const checkFileTypeOnLoad = (fileLink) => {
        if (fileLink.includes("image")) {
            return "image";
        }
        if (fileLink.includes("video")) {
            return "video";
        }
        return "";
    };

    useEffect(() => {
        if (adToEdit !== null) {
            setHeadline(adToEdit.headline);
            setDescription(adToEdit.description);
            setButtonText(adToEdit.buttonText);
            setButtonLink(adToEdit.buttonLink);
            setAudience(adToEdit.audience);
            const selectedPlacementOptions = filteredPlacementOptions.filter(option =>
                adToEdit.placement.includes(Number(option.value))
            );
            setPlacement(selectedPlacementOptions);
            setStartsAt(adToEdit.startsAt);
            setEndsAt(adToEdit.endsAt);
            setFormPrimaryAsset((prev) => ({
                ...prev,
                primaryAsset: adToEdit.primaryAsset,
                primaryAssetFile: adToEdit.primaryAsset.assetUrl,
                primaryAssetBlob: adToEdit.primaryAsset.assetUrl,
            }));

            setFormPrimaryAssetType(checkFileTypeOnLoad(adToEdit.primaryAsset.assetType));
            if (adToEdit.verticalAsset) {
                setFormVerticalAsset((prev) => ({
                    ...prev,
                    verticalAsset: adToEdit.verticalAsset,
                    verticalAssetFile: adToEdit.verticalAsset.assetUrl,
                    verticalAssetBlob: adToEdit.verticalAsset.assetUrl,
                }));
                setFormVerticalAssetType(checkFileTypeOnLoad(adToEdit.verticalAsset.assetType));
                setFormVerticalHasAsset(true);
            }
        }
    }, [adToEdit]);

    const inputStartDate = (selectedDate) => {
        setStartsAt(selectedDate);
    };

    const inputFinishDate = (selectedDate) => {
        setEndsAt(selectedDate);
    };

    const handleFormSubmission = () =>
        handleFormSubmissionLogic(adToEdit ? "edit" : "create");

    const handleFormSubmissionLogic = async (type) => {
        const formatter = Intl.DateTimeFormat("fr-CA", { year: "numeric", month: "2-digit", day: "2-digit" });
        const formattedStartDate = startsAt ? formatter.format(new Date(startsAt)) : "";
        const formattedEndDate = endsAt ? formatter.format(new Date(endsAt)) : "";
        const formattedPlacementValue = placement ? placement.map(item => Number(item.value)) : [];
        const isEditingEntry = type === "edit";

        let formRequest = {
            headline,
            description,
            audience,
            buttonText,
            buttonLink,
            placement: formattedPlacementValue,
            startsAt: `${formattedStartDate} 00:00:00`,
            endsAt: `${formattedEndDate} 23:59:59`,
        };

        let adStatusType = FILTER_BY.ACTIVE_ADS;
        if (startsAt > new Date) {
            adStatusType = FILTER_BY.SCHEDULED_ADS;
        }

        if (controllerRef.current) {
            controllerRef.current.abort();
        }

        setIsFormSending(true);
        controllerRef.current = new AbortController();

        const primaryAssetForValidation = formPrimaryAssetChanged
            ? formPrimaryAsset.primaryAssetFile
            : getExistingPrimaryAssetUrl(formPrimaryAsset, adToEdit);
        const errors = validateAd(formRequest, primaryAssetForValidation);
    
        if (errors.length > 0) {
            setInputErrors(errors);
            setIsFormSending(false);
            return;
        }
    
        const processAssetUpload = async (selectedFile, assetType, controllerRef) => {
            let fileToUpload;
            const fileType = selectedFile?.type?.replace(/^(image|video)\//, "");
            const mediaType = selectedFile?.type?.startsWith("video/") ? "video" : "image";

            fileToUpload = new File([selectedFile], `ad-${assetType}-asset.${fileType}`);
            const presignedUrl = await requestS3PresignedUrl(fileType, controllerRef);
            const uploadedS3Url = await uploadToS3(presignedUrl.url, fileToUpload, controllerRef);
            return {
                url: uploadedS3Url,
                uuid: presignedUrl.uuid,
                fileType,
                mediaType,
            };
        };
    
        if (formPrimaryAssetChanged) {
            const formPrimaryResult = await processAssetUpload(formPrimaryAsset.primaryAssetFile, "primary", controllerRef);
            formRequest.primaryAsset = {
                assetUrl: formPrimaryResult.url,
                aspectRatio: "widescreen",
                videoKey: formPrimaryResult.mediaType === "video" ? formPrimaryResult.uuid : null,
                assetType: formPrimaryResult.mediaType,
                trimStart: formPrimaryAsset.trimStart ?? 0,
                trimEnd: formPrimaryAsset.trimEnd ?? 0,
                crop: {
                    x: formPrimaryAsset.crop?.croppedAreaPixels?.x ?? 0,
                    y: formPrimaryAsset.crop?.croppedAreaPixels?.y ?? 0,
                    width: formPrimaryAsset.crop?.croppedAreaPixels?.width ?? 0,
                    height: formPrimaryAsset.crop?.croppedAreaPixels?.height ?? 0
                }
            };
        } else {
            const existingPrimaryAsset = getExistingPrimaryAsset(formPrimaryAsset, formPrimaryAssetType, adToEdit);
            if (existingPrimaryAsset.assetUrl) {
                formRequest.primaryAsset = existingPrimaryAsset;
            } else {
                Toast.error(ErrorStrings[ErrorList.EMPTY_PRIMARY_ASSET]);
                setIsFormSending(false);
                return;
            }
        }

        if (formVerticalHasAsset && formVerticalAsset.verticalAssetFile instanceof File) {
            const verticalAssetResult = await processAssetUpload(formVerticalAsset.verticalAssetFile, "vertical", controllerRef);
            formRequest.verticalAsset = {
                assetUrl: verticalAssetResult.url,
                aspectRatio: "portrait",
                videoKey: verticalAssetResult.mediaType === "video" ? verticalAssetResult.uuid : null,
                assetType: verticalAssetResult.mediaType,
                trimStart: formVerticalAsset.trimStart ?? 0,
                trimEnd: formVerticalAsset.trimEnd ?? 0,
                crop: {
                    x: formVerticalAsset.crop?.croppedAreaPixels?.x ?? 0,
                    y: formVerticalAsset.crop?.croppedAreaPixels?.y ?? 0,
                    width: formVerticalAsset.crop?.croppedAreaPixels?.width ?? 0,
                    height: formVerticalAsset.crop?.croppedAreaPixels?.height ?? 0
                }
            };
        } else if (formVerticalAsset.verticalAsset || adToEdit?.verticalAsset) {
            formRequest.verticalAsset = getExistingVerticalAsset(formVerticalAsset, formVerticalAssetType, adToEdit);
        } else {
            formRequest.verticalAsset = null;
        }

        const apiUrl = isEditingEntry ? `${API_URL}/advertising/${adToEdit.uuid}` : `${API_URL}/advertising`;
        const apiMethod = isEditingEntry ? axios.put : axios.post;

        apiMethod(apiUrl, formRequest, {
            headers: { Authorization: `Bearer ${currentUser[UserKeys.TOKEN]}` },
            signal: controllerRef.current.signal,
        })
            .then(() => {
                Toast.success(`Ad ${isEditingEntry ? "updated" : "created"} successfully`);
                handleHideAdForm();
                setIsFormSending(false);
                setFilterOption(adStatusType);
                const urlEncodedAudience = (Object.values(AUDIENCE).find(i => i === audience.replace("&", "%26")));
                setAudienceOption(urlEncodedAudience);
                fetchCompanyAds(adStatusType, urlEncodedAudience);
            })
            .catch((error) => {
                Toast.error(error.getApiErrorDetails().message);
                setIsFormSending(false);
            });
    };

    return (
        <PortalFormSurface maxWidth="94.5rem" marginTop="0" flex="1 1 auto" minHeight="0">
            <Form width="100%" paddingBottom="0" onSubmit={(e) => { e.preventDefault(); }} style={{ margin: 0, display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }} >
                <PortalFormScrollBody columns="minmax(20rem, 0.85fr) minmax(0, 1.15fr)" padding="1.25rem" gap="1rem">
                    <PortalFormSection>
                        <PortalFormSectionHeader withDivider>
                            <PortalFormSectionTitle>
                                Ad Details
                            </PortalFormSectionTitle>
                            <PortalFormSectionDescription>
                                Write the copy, choose the audience and schedule the campaign.
                            </PortalFormSectionDescription>
                        </PortalFormSectionHeader>
                        <HeadlineField headline={headline} inputHeadline={setHeadline} inputErrors={inputErrors} />
                        <AdDescriptionField description={description} inputDescription={setDescription} inputErrors={inputErrors} />
                        <ButtonTextField buttonText={buttonText} inputButtonText={setButtonText} inputErrors={inputErrors} />
                        <ButtonLinkField buttonLink={buttonLink} inputButtonLink={setButtonLink} inputErrors={inputErrors} />
                        <AudienceSelectionField selectAudience={setAudience} inputErrors={inputErrors} audience={audience} />
                        <PlacementSelectionField selectPlacement={setPlacement} inputErrors={inputErrors} placement={placement} />
                        <AdDurationFields inputStartDate={inputStartDate} inputFinishDate={inputFinishDate} inputErrors={inputErrors} startsAt={startsAt} endsAt={endsAt} isEditForm={ adToEdit !== null } />
                    </PortalFormSection>
                    <FormRightSection>
                        <PortalFormSection>
                            <PortalFormSectionHeader withDivider>
                                <PortalFormSectionTitle>
                                    Upload Image/Video
                                </PortalFormSectionTitle>
                                <PortalFormSectionDescription>
                                    Add campaign media for widescreen and optional portrait placements.
                                </PortalFormSectionDescription>
                            </PortalFormSectionHeader>
                            <FormAssetSection>
                                <AdPrimaryImageUpload
                                    asset={formPrimaryAsset}
                                    setFormPrimaryAsset={setFormPrimaryAsset}
                                    setImageChanged={setFormPrimaryAssetChanged}
                                    setFormPrimaryAssetType={setFormPrimaryAssetType}
                                    inputErrors={inputErrors}
                                    assetType={formPrimaryAssetType}
                                />
                                <AdVerticalImageUpload
                                    asset={formVerticalAsset}
                                    setFormVerticalAssetType={setFormVerticalAssetType}
                                    setFormVerticalAsset={setFormVerticalAsset}
                                    setImageChanged={setFormVerticalHasAsset}
                                    assetType={formVerticalAssetType}
                                />
                            </FormAssetSection>
                        </PortalFormSection>
                        <PortalFormSection>
                            <div>
                                <PortalFormSectionTitle>
                                    Preview Ad
                                </PortalFormSectionTitle>
                            </div>
                            <AdFormContext.Provider value={{
                                headlineContext: headline,
                                descriptionContext: description,
                                buttonTextContext: buttonText,
                                formPrimaryAssetContext: formPrimaryAsset.primaryAssetBlob,
                                formPrimaryAssetChangedContext: formPrimaryAssetChanged,
                                formPrimaryAssetTypeContext: formPrimaryAssetType,
                                formVerticalAssetContext: formVerticalAsset.verticalAssetBlob,
                                formVerticalAssetTypeContext: formVerticalAssetType,
                                formVerticalHasAssetContext: formVerticalHasAsset,
                                isEditForm: adToEdit !== null,
                                companyColors: companyColors
                            }}>
                                {
                                    !companyColorsReady ? <Skeleton width="45%" height="500px" animation="wave" /> :
                                        <PhoneContainer>
                                            <ImageSlider
                                                companyName={currentUser.companyName}
                                                logoTopOffset={45}
                                                phoneFrameTopOffset={30}
                                                assetLogoUrl={currentUser.companyLogoUrl}
                                                getLabelForPreview={getLabelForPreview}
                                                companyColors={companyColors}
                                            />
                                        </PhoneContainer>
                                }
                            </AdFormContext.Provider>
                        </PortalFormSection>
                    </FormRightSection>
                </PortalFormScrollBody>
                <PortalFormActions sticky={false}>
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton type="button" onClick={() => handleHideAdForm()} disabled={isFormSending}>
                            Cancel
                        </StandardModalActionButton>
                        <StandardModalActionButton
                            type="button"
                            $variant="primary"
                            onClick={() => { handleFormSubmission(); }}
                            disabled={isFormSending}
                        >
                            {isFormSending
                                ? <LottieLoadingIndicator height="1.5rem" width="1.5rem" containerHeight="1.5rem" margin="0" />
                                : "Save"}
                        </StandardModalActionButton>
                    </StandardModalActionGroup>
                </PortalFormActions>
            </Form>
        </PortalFormSurface>
    );
};

export default AdvertisingForm;
